import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  ShieldCheck,
  Home as HomeIcon,
  Waves,
  ArrowRight,
  Star,
  Phone,
  Mail,
  MapPin,
  Check,
  ExternalLink,
} from "lucide-react";
import {
  BUSINESS,
  SERVICES,
  TESTIMONIALS,
  TEAM,
  WHY_CHOOSE,
  MISSION,
  IMAGES,
} from "../data/site";
import TrustRibbon from "../components/TrustRibbon";
import FinalCTA from "../components/FinalCTA";
import Newsletter from "../components/Newsletter";

const whyIcons = { home: HomeIcon, award: Award, waves: Waves, shield: ShieldCheck };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden hero-glow"
        style={{ background: "linear-gradient(180deg, #EAF3FF 0%, #F5F9FF 60%, #FFFFFF 100%)" }}
      >
        <div className="aqua-container pt-12 pb-16 lg:pt-20 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase"
                style={{ backgroundColor: "#EAF3FF", color: "#1E7BE0" }}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {BUSINESS.yearsExperience} Years • Certified • Insured
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-5 text-[#0B2F6B] leading-[1.05]">
                Where confidence begins in{" "}
                <span className="text-gradient-aqua">every wave.</span>
              </h1>
              <p className="mt-6 text-[#3B5878] text-base sm:text-lg leading-relaxed max-w-xl">
                Premium mobile swim lessons, small-group academy classes, and
                private lifeguarding across Southeast North Carolina — for ages 6
                months to adult. Expert, caring instruction brought right to you.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={BUSINESS.registrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-cta"
                  className="btn-book"
                >
                  Book Now <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/about" data-testid="hero-learn-cta" className="btn-outline">
                  Learn More
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#3B5878]">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#33BED6] text-[#33BED6]" />
                    ))}
                  </div>
                  <span className="font-medium text-[#0B2F6B]">5.0 on Google</span>
                </div>
                <span className="hidden sm:inline text-[#DCE7F5]">|</span>
                <span className="italic text-[#0B2F6B] font-heading">"swim with flow."</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] -z-10" style={{ backgroundColor: "#EAF3FF" }} />
                <img
                  src={IMAGES.hero}
                  alt="Swim instructor teaching a child to swim in a pool in North Carolina"
                  loading="eager"
                  className="w-full h-[400px] sm:h-[500px] lg:h-[560px] object-cover rounded-3xl shadow-lg"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-5 -left-5 sm:-left-8 bg-white rounded-2xl shadow-xl p-4 sm:p-5 max-w-[260px] border border-[#DCE7F5]">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: "#1E7BE0" }}>
                      <Award className="w-5 h-5 text-white" />
                    </span>
                    <div>
                      <p className="font-heading font-semibold text-[#0B2F6B]">Coach Félix Rivera</p>
                      <p className="text-xs text-[#3B5878]">{BUSINESS.yearsExperience} years in aquatics</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <TrustRibbon />

      {/* WHY FAMILIES CHOOSE */}
      <section className="aqua-section">
        <div className="aqua-container">
          <div className="max-w-2xl mb-12">
            <p className="text-eyebrow">Why families choose us</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 text-[#0B2F6B]">
              Why families choose Agua Flow Mobile.
            </h2>
            <p className="mt-4 text-[#3B5878] text-base sm:text-lg leading-relaxed">
              {MISSION.short}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = whyIcons[w.icon] || Waves;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="card-soft p-7"
                  data-testid={`why-choose-${i}`}
                >
                  <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                    <Icon className="w-6 h-6 text-[#1E7BE0]" />
                  </span>
                  <h3 className="font-heading text-lg font-semibold mt-5 text-[#0B2F6B]">{w.title}</h3>
                  <p className="mt-2 text-[#3B5878] leading-relaxed text-sm">{w.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="aqua-section pt-0">
        <div className="aqua-container">
          <div className="max-w-2xl mb-12">
            <p className="text-eyebrow">Our services</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 text-[#0B2F6B]">
              Aquatic services for every stage.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card-soft overflow-hidden flex flex-col"
                data-testid={`home-service-card-${s.id}`}
              >
                <div className="relative">
                  <img
                    src={s.image}
                    alt={`${s.name} — Agua Flow Mobile`}
                    loading="lazy"
                    className="w-full h-48 object-cover"
                  />
                  {s.tag && (
                    <span
                      className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: s.comingSoon ? "#7C93AD" : "#1E7BE0" }}
                    >
                      {s.tag}
                    </span>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading text-xl font-semibold text-[#0B2F6B]">{s.name}</h3>
                  <p className="mt-2 text-[#3B5878] text-sm flex-1">{s.short}</p>
                  <Link
                    to="/programs"
                    className="btn-ghost mt-5 self-start text-sm"
                    data-testid={`home-service-learn-${s.id}`}
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* SAFETY STATS */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: "#EAF3FF" }}>
        <div className="aqua-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-eyebrow" style={{ color: "#1E7BE0" }}>Why swim safety matters</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 leading-tight text-[#0B2F6B]">
              Drowning is the leading cause of death for children ages 1–4.
            </h2>
            <p className="mt-5 text-[#3B5878] text-base sm:text-lg leading-relaxed">
              Early, professional swim instruction can reduce the risk of drowning
              by up to 88% in young children. Water safety isn't optional — it's
              one of the most important skills a child can learn.
            </p>
            <a
              href={BUSINESS.registrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="safety-cta"
              className="btn-book mt-8"
            >
              Start Lessons Today
            </a>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              { num: "88%", label: "Reduced drowning risk with formal swim lessons" },
              { num: "1–4", label: "Highest-risk age for accidental drowning" },
              { num: "6 mo", label: "When water safety education can begin" },
              { num: "23+", label: "Years of Coach Félix's experience" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-[#DCE7F5] bg-white p-6 shadow-sm" data-testid={`safety-stat-${i}`}>
                <p className="font-heading text-3xl sm:text-4xl font-bold text-[#1E7BE0]">{s.num}</p>
                <p className="mt-2 text-sm text-[#3B5878]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="aqua-section">
        <div className="aqua-container">
          <div className="max-w-2xl mb-12">
            <p className="text-eyebrow">Meet the team</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 text-[#0B2F6B]">
              The people behind the flow.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="card-soft overflow-hidden group"
                data-testid={`home-team-${i}`}
              >
                <div className="overflow-hidden">
                  {m.image ? (
                    <img
                      src={m.image}
                      alt={`${m.name}, ${m.role}`}
                      loading="lazy"
                      style={{ objectPosition: m.imagePosition || "center" }}
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="w-full h-64 flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg, #1E7BE0, #33BED6)" }}
                    >
                      <span className="font-script text-6xl text-white">
                        {m.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-[#0B2F6B]">{m.name}</h3>
                  <p className="text-[#1E7BE0] text-sm font-medium">{m.role}</p>
                  <p className="mt-3 text-[#3B5878] text-sm leading-relaxed">{m.bio}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {m.certifications.map((c) => (
                      <span key={c} className="text-xs px-3 py-1 rounded-full bg-[#EAF3FF] text-[#1E7BE0] font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="aqua-section pt-0">
        <div className="aqua-container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-eyebrow">What families say</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 text-[#0B2F6B]">
                Loved by families across NC.
              </h2>
            </div>
            <a
              href={BUSINESS.reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-cta-home"
              className="btn-outline self-start sm:self-auto"
            >
              Leave a Google Review <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="card-soft p-6"
                data-testid={`home-testimonial-${i}`}
              >
                <div className="flex mb-3">
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-[#33BED6] text-[#33BED6]" />
                  ))}
                </div>
                <p className="text-[#0B2F6B] leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 pt-4 border-t border-[#DCE7F5]">
                  <p className="font-heading font-semibold text-[#0B2F6B]">{t.name}</p>
                  <p className="text-sm text-[#7C93AD]">{t.role} • {t.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/reviews" className="btn-ghost" data-testid="see-all-reviews">
              See all reviews <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK CONTACT */}
      <section className="pb-8">
        <div className="aqua-container">
          <div className="rounded-3xl border border-[#DCE7F5] bg-white p-8 sm:p-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href={BUSINESS.phoneHref} className="flex items-start gap-4 group" data-testid="quick-contact-phone">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <Phone className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Call</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B] group-hover:text-[#1E7BE0]">{BUSINESS.phone}</p>
              </div>
            </a>
            <a href={BUSINESS.emailHref} className="flex items-start gap-4 group" data-testid="quick-contact-email">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <Mail className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Email</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B] group-hover:text-[#1E7BE0] break-all">{BUSINESS.email}</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <MapPin className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Service area</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B]">Southeast NC</p>
                <p className="text-sm text-[#3B5878] mt-0.5">Jacksonville • Wilmington • Surf City + more</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />

      <FinalCTA />
    </div>
  );
}
